import { Checkbox, FormControlLabel, Stack, TextField, Typography } from '@mui/material';
import { VisitorGroup } from 'graphql/generated/graphqlTypes';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { lowerCase, upperFirst } from 'lodash';
import { useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { isOfficeModeEnabledForVisitorGroup, isQRInviteEnabledForVisitorGroup } from 'utils/VisitorGroup/VisitorGroup';
import DrawerTemplate from 'v2/components/Drawer/DrawerTemplate';
import useDrawerContext from 'v2/components/Drawer/useDrawerContext';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import useExternalUsers from 'v2/hooks/useExternalUsers/useExternalUsers';
import { areQrCodeVisitsEnabled } from 'v2/utils/areQrCodeVisitsEnabled';
import { canEditVisitorGroup } from 'v2/utils/visitorGroupOrigin';

interface FormValues {
  name: string;
  isMaximumUserLimit: boolean;
  maxNumberOfMembers: number;
  isOfficeMode: boolean;
  isQRInvites: boolean;
}

const DEFAULT_MAX_NUMBER_OF_MEMBERS = 10;
const MAX_MEMBERS_INPUT_LIMIT = 999;

interface Props {
  visitorGroup: VisitorGroup;
}

export const FORM_ID = 'edit-group-drawer-form';

const EditGroupDrawer = ({ visitorGroup }: Props) => {
  const { translation } = useTranslation();
  const { closeDrawer } = useDrawerContext();
  const { showSnackbar } = useSnackbarContext();
  const [error, setError] = useState<string>();
  const { externalUsersGroups, updateVisitorGroupDetails } = useExternalUsers({
    translation,
    handleFetchError: setError
  });

  const canEdit = canEditVisitorGroup(visitorGroup);
  const qrCodeVisitsEnabled = areQrCodeVisitsEnabled();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting }
  } = useForm<FormValues>({
    mode: 'onTouched',
    defaultValues: {
      name: visitorGroup.name,
      isMaximumUserLimit: visitorGroup.maxNumberOfMembers != null,
      maxNumberOfMembers: visitorGroup.maxNumberOfMembers ?? DEFAULT_MAX_NUMBER_OF_MEMBERS,
      isOfficeMode: isOfficeModeEnabledForVisitorGroup(visitorGroup),
      isQRInvites: isQRInviteEnabledForVisitorGroup(visitorGroup)
    }
  });

  const isMaximumUserLimitEnabled = useWatch({ control, name: 'isMaximumUserLimit' });

  const onSubmit = async (data: FormValues) => {
    setError(undefined);
    const result = await updateVisitorGroupDetails({
      visitorGroup,
      name: canEdit ? data.name : undefined,
      // eslint-disable-next-line no-nested-ternary
      maxNumberOfMembers: canEdit ? (data.isMaximumUserLimit ? data.maxNumberOfMembers : null) : undefined,
      isOfficeModeEnabled: data.isOfficeMode,
      isQrCodeVisitInviter: qrCodeVisitsEnabled ? data.isQRInvites : false
    });
    if (!result.success) return;
    showSnackbar(translation.saved_changes, { severity: 'success' });
    closeDrawer();
  };

  return (
    <DrawerTemplate
      loading={isSubmitting}
      id="edit-group-drawer"
      title={translation.edit_group}
      primaryAction={{
        label: translation.save,
        onClick: handleSubmit(onSubmit),
        loading: isSubmitting,
        form: FORM_ID,
        id: 'save-button'
      }}
      secondaryAction={{ label: translation.cancel, id: 'cancel-button' }}
      error={error}
      onDismissError={() => setError(undefined)}
    >
      <Stack id={FORM_ID} component="form" onSubmit={handleSubmit(onSubmit)} noValidate gap={3} mt={1}>
        {canEdit && (
          <TextField
            {...register('name', {
              required: translation.name_cannot_be_empty,
              validate: (value) =>
                !externalUsersGroups.filter((g) => g.id !== visitorGroup.id).some((g) => g.name === value) ||
                translation.group_name_must_be_unique
            })}
            id="name-input"
            label={translation.name}
            variant="outlined"
            error={!!errors.name}
            helperText={errors.name?.message}
          />
        )}
        {canEdit && (
          <>
            <Controller
              name="isMaximumUserLimit"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      id="isMaximumUserLimit-checkbox"
                      checked={field.value}
                      onChange={field.onChange}
                      color="primary"
                    />
                  }
                  label={translation.set_maximum}
                />
              )}
            />
            {isMaximumUserLimitEnabled && (
              <TextField
                {...register('maxNumberOfMembers', {
                  valueAsNumber: true,
                  validate: (value) => {
                    if (!isMaximumUserLimitEnabled) return true;
                    if (!value) return translation.maximum_users_cannot_be_empty;
                    if (!Number.isInteger(value)) return translation.maximum_users_whole_number;
                    if (value < 1) return translation.maximum_users_positive;
                    if (value < visitorGroup.members.length)
                      return translation.value_cannot_be_lower_than_current_amount_of_users_in_the_group;
                    return true;
                  }
                })}
                id="maxNumberOfMembers-input"
                label={upperFirst(lowerCase(translation.maximum_users))}
                type="number"
                variant="outlined"
                disabled={!isMaximumUserLimitEnabled}
                slotProps={{ htmlInput: { min: 1, max: MAX_MEMBERS_INPUT_LIMIT, step: 1 } }}
                error={!!errors.maxNumberOfMembers}
                helperText={errors.maxNumberOfMembers?.message ?? ' '}
              />
            )}
          </>
        )}
        <Typography variant="subtitle1">{translation.office_mode}</Typography>
        <Typography variant="body2">{translation.office_mode_permission_description}</Typography>
        <Controller
          name="isOfficeMode"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Checkbox id="isOfficeMode-checkbox" checked={field.value} onChange={field.onChange} color="primary" />
              }
              label={translation.allow_office_mode}
            />
          )}
        />
        {qrCodeVisitsEnabled && (
          <>
            <Typography variant="subtitle1">{translation.qr_invites}</Typography>
            <Typography variant="body2">{translation.qr_invites_permission_description}</Typography>
            <Controller
              name="isQRInvites"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      id="isQRInvites-checkbox"
                      checked={field.value}
                      onChange={field.onChange}
                      color="primary"
                    />
                  }
                  label={translation.allow_qr_invites}
                />
              )}
            />
          </>
        )}
      </Stack>
    </DrawerTemplate>
  );
};

export default EditGroupDrawer;
